import React from 'react';
import { BUILD_UPDATE_COPY } from '../constants/buildUpdate';
import { useBuildUpdateCheck, VersionFileFetcher } from '../hooks/useBuildUpdateCheck';

interface UpdateAvailableBannerProps {
  // Overridable for tests; production reads the build-time values.
  runningVersion?: string;
  isEnabled?: boolean;
  fetchVersionFile?: VersionFileFetcher;
  onRefresh?: () => void;
}

const reloadPage = () => window.location.reload();

/**
 * Pinned, non-dismissible prompt shown once this site serves a newer build than
 * the tab is running. Mounted once at the app root so it covers every route,
 * including login. Identical in frontend, admin-portal and superadmin-portal.
 */
const UpdateAvailableBanner: React.FC<UpdateAvailableBannerProps> = ({
  runningVersion = process.env.REACT_APP_VERSION,
  // The dev server has no version.txt; only built bundles check.
  isEnabled = process.env.NODE_ENV === 'production',
  fetchVersionFile,
  onRefresh = reloadPage,
}) => {
  const isUpdateAvailable = useBuildUpdateCheck({ runningVersion, isEnabled, fetchVersionFile });

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-[2000] flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-4 rounded-card bg-[var(--ink-canvas)] py-3 pl-5 pr-3 font-sans text-sm text-[var(--on-dark)] shadow-[var(--elev-dialog)]"
    >
      <span>{BUILD_UPDATE_COPY.message}</span>
      <button
        type="button"
        onClick={onRefresh}
        className="rounded-control bg-signature px-4 py-1.5 font-semibold text-on-signature hover:bg-signature-strong focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
      >
        {BUILD_UPDATE_COPY.action}
      </button>
    </div>
  );
};

export default UpdateAvailableBanner;
